import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "content", "filename", "filesize", "downloadLink", "navPrev", "navNext"]

  // All previewable attachments in the current chat for gallery navigation
  attachments = []
  currentIndex = 0

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.dialogTarget.addEventListener("close", () => this.cleanup())
  }

  disconnect() {
    this.cleanup()
  }

  open(event) {
    event.preventDefault()

    const trigger = event.currentTarget
    const url = trigger.dataset.attachmentUrl
    const contentType = trigger.dataset.attachmentContentType
    const filename = trigger.dataset.attachmentFilename
    const filesize = trigger.dataset.attachmentFilesize
    const downloadUrl = trigger.dataset.attachmentDownloadUrl

    // Collect all previewable attachments for gallery navigation
    this.collectAttachments()
    this.currentIndex = this.attachments.findIndex(a => a.url === url)

    this.show({ url, contentType, filename, filesize, downloadUrl })
  }

  show({ url, contentType, filename, filesize, downloadUrl }) {
    this.contentTarget.innerHTML = ""
    this.filenameTarget.textContent = filename || ""
    this.filesizeTarget.textContent = filesize || ""
    this.downloadLinkTarget.href = downloadUrl || url

    if (this.isImage(contentType)) {
      const img = document.createElement("img")
      img.src = url
      img.alt = filename || "Image"
      img.className = "max-w-full max-h-[75vh] object-contain rounded-lg"
      img.loading = "eager"
      this.contentTarget.appendChild(img)
    } else if (this.isPdf(contentType)) {
      const iframe = document.createElement("iframe")
      iframe.src = url
      iframe.className = "w-full rounded-lg border-0"
      iframe.style.height = "75vh"
      iframe.title = filename || "Document"
      this.contentTarget.appendChild(iframe)
    } else if (this.isWord(contentType)) {
      // Word docs can't be rendered inline — show download prompt
      const container = document.createElement("div")
      container.className = "flex flex-col items-center justify-center py-16 gap-4"
      container.innerHTML = `
        <svg class="w-16 h-16 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
        <p class="chat-text-body text-sm">This file type cannot be previewed inline.</p>
        <a href="${downloadUrl || url}" class="send-btn px-4 py-2 text-sm rounded-lg no-underline" download>Download File</a>
      `
      this.contentTarget.appendChild(container)
    }

    // Show/hide nav arrows
    this.updateNav()

    this.dialogTarget.showModal()
    document.addEventListener("keydown", this.handleKeydown)
  }

  close() {
    this.dialogTarget.close()
  }

  cleanup() {
    document.removeEventListener("keydown", this.handleKeydown)
    this.contentTarget.innerHTML = ""
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--
      this.show(this.attachments[this.currentIndex])
    }
  }

  next() {
    if (this.currentIndex < this.attachments.length - 1) {
      this.currentIndex++
      this.show(this.attachments[this.currentIndex])
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    } else if (event.key === "ArrowLeft") {
      this.prev()
    } else if (event.key === "ArrowRight") {
      this.next()
    }
  }

  backdropClick(event) {
    // Close when clicking the backdrop (the dialog element itself, not its children)
    if (event.target === this.dialogTarget) {
      this.close()
    }
  }

  collectAttachments() {
    const triggers = this.element.querySelectorAll("[data-action*='attachment-lightbox#open']")
    this.attachments = Array.from(triggers).map(el => ({
      url: el.dataset.attachmentUrl,
      contentType: el.dataset.attachmentContentType,
      filename: el.dataset.attachmentFilename,
      filesize: el.dataset.attachmentFilesize,
      downloadUrl: el.dataset.attachmentDownloadUrl
    }))
  }

  updateNav() {
    if (this.hasNavPrevTarget) {
      this.navPrevTarget.classList.toggle("invisible", this.currentIndex <= 0)
    }
    if (this.hasNavNextTarget) {
      this.navNextTarget.classList.toggle("invisible", this.currentIndex >= this.attachments.length - 1)
    }
  }

  isImage(contentType) {
    return contentType && contentType.startsWith("image/")
  }

  isPdf(contentType) {
    return contentType === "application/pdf"
  }

  isWord(contentType) {
    return contentType && (
      contentType === "application/msword" ||
      contentType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    )
  }
}
